import { zodResolver } from '@hookform/resolvers/zod';
import {
  AgendaItem,
  AgendaItemDoc,
  SpeakerDoc,
  TrackDoc,
  agendaItemFormValuesSchema,
} from '@livekatsomo/models';
import { useDateFns, useEnvironment } from '@livekatsomo/web/hooks';
import {
  MultipleSelectField,
  RadioField,
  SwitchField,
  TextField,
  TimePickerField,
} from '@livekatsomo/web/ui-components/react-hook-form-components';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import { addMilliseconds, addMinutes } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAgendaItemsWithStartTime } from '../useAgendaItemsWithStartTime/useAgendaItemsWithStartTime';

/**
 * Props for the AgendaForm component.
 */
export interface AgendaFormProps {
  agendaItem?: AgendaItemDoc | null;
  agendaItems: AgendaItemDoc[] | null;
  eventStartDate: Date;
  speakers?: SpeakerDoc[] | null;
  track: TrackDoc;
  onClose: () => void;
  onSubmit: (values: AgendaItem) => Promise<void>;
}

/**
 * A form component for adding or editing an agenda item.
 * @param agendaItem - The agenda item to edit, if any.
 * @param speakers - An array of speakers to choose from, if the agenda item is a talk.
 * @param track - The track the agenda item belongs to.
 * @param onClose - A function to call when the form is closed.
 * @param onSubmit - A function to call when the form is submitted.
 */
export function AgendaForm({
  agendaItem,
  agendaItems,
  eventStartDate,
  speakers,
  track,
  onClose,
  onSubmit,
}: AgendaFormProps) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const { development } = useEnvironment();
  const { t } = useTranslation();
  const { format } = useDateFns();
  const initialValues = {
    title: '',
    duration: 0,
    description: '',
    speakers: [],
    location: '',
    showTime: true,
    trackId: track.id,
    timestamp: null,
    type: agendaItem?.type || 'talk',
    // Will be set to true if the creating new agenda item.
    showSpeakers: agendaItem?.showSpeakers ? true : !agendaItem ? true : false,
    includeSpeakerDescription: false,
    ...agendaItem,
  };
  const agendaItemsWithDates = useAgendaItemsWithStartTime(
    agendaItems || [],
    eventStartDate,
    track.startTimeDelta,
    track.mode,
    track.currentAgendaItemId,
  );

  const startTimeForAgendaItem = agendaItem
    ? agendaItemsWithDates.find((item) => item.id === agendaItem.id)?.startTime
    : agendaItemsWithDates.length
      ? addMinutes(
          agendaItemsWithDates[agendaItemsWithDates.length - 1]?.startTime,
          agendaItemsWithDates[agendaItemsWithDates.length - 1]?.duration,
        )
      : addMilliseconds(eventStartDate, track.startTimeDelta);

  const methods = useForm({
    resolver: zodResolver(agendaItemFormValuesSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const {
    control,
    formState: { isDirty, isSubmitting, isValid },
    setValue,
    reset,
    handleSubmit,
    watch,
  } = methods;

  const handleClose = () => {
    setErrorMessage(undefined);
    reset();
    onClose();
  };

  const submit = async (values: typeof initialValues) => {
    console.log('agenda form submitted', values);

    try {
      await onSubmit({ ...values });
      handleClose();
    } catch (error) {
      setErrorMessage((error as Error).message);
    }
  };

  const [type, timestamp, duration, showSpeakers] = watch([
    'type',
    'timestamp',
    'duration',
    'showSpeakers',
  ]);

  const startTime = startTimeForAgendaItem
    ? format(startTimeForAgendaItem, 'HH:mm')
    : undefined;
  const endTime = startTimeForAgendaItem
    ? format(addMinutes(startTimeForAgendaItem, duration), 'HH:mm')
    : undefined;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)} onReset={() => reset()}>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <DialogContentText
            sx={{
              mb: 2,
            }}
          >
            {t('Add agenda details here.')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label={t('title')}
            type="text"
            fullWidth
            required
            variant="standard"
          />
          <TextField
            autoFocus
            margin="dense"
            name="description"
            label={t('description')}
            type="text"
            fullWidth
            variant="standard"
          />
          <TextField
            margin="dense"
            name="duration"
            label={t('duration')}
            type="number"
            helperText={t('Duration in minutes')}
            fullWidth
            required
            variant="standard"
          />
          <Typography data-chromatic="ignore">
            {startTime} - {endTime}
          </Typography>
          <RadioField
            name="type"
            label={t('type')}
            options={['talk', 'break']}
          />

          {timestamp ? (
            <TimePickerField label={t('Time Stamp')} name="timestamp" />
          ) : null}

          <SwitchField name="showTime" label={t('Show time')} />
          {type === 'talk' && speakers && speakers.length ? (
            <>
              <MultipleSelectField
                control={control}
                name="speakers"
                label={t('Speakers')}
                options={speakers}
                getOptionLabel={(speaker) => speaker.name}
              />
              <SwitchField name="showSpeakers" label={t('Show Speakers')} />
              {showSpeakers && (
                <SwitchField
                  control={control}
                  name="includeSpeakerDescription"
                  label={t('Include Speaker Description')}
                />
              )}
            </>
          ) : null}
          {errorMessage ? (
            <Typography
              color="error"
              variant="body1"
              sx={{
                textAlign: 'center',
                mt: 2,
              }}
            >
              {errorMessage}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          {development ? (
            <Button
              type="button"
              variant="outlined"
              onClick={async (e) => {
                const generateAgendaFormContent = (
                  await import(
                    '@livekatsomo/web/ui-components/random-data-generators'
                  )
                ).generateAgendaFormContent;
                generateAgendaFormContent(track.id, setValue, speakers);
              }}
            >
              {t('Generate')}
            </Button>
          ) : null}
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="reset" disabled={isSubmitting || !isDirty}>
            {t('Reset')}
          </Button>
          <LoadingButton
            type="submit"
            disabled={!isValid || isSubmitting || !isDirty}
            loading={isSubmitting}
          >
            {agendaItem ? t('Update') : t('Add')}
          </LoadingButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
}
